import { store, store$ } from '@redux/store';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  tap,
  distinctUntilKeyChanged,
  fromEvent
} from 'rxjs';
import {
  createHoveredMarker,
  getMapBounds,
  paintMarker,
  removeHoveredMarker,
  removeMarkers,
  updateMarkerContent,
  getMapZoom,
  updateMarkerGroupsByPriority,
  updateMarkersVisibility
} from 'libraries/mapLibrary';
import uiStateSlice from 'layout/redux/uiStateSlice';
import {
  LocationType,
  selectLocationType
} from 'features/searchResultsFacets';
import { selectLayoutBreakpoint } from 'layout/redux/selectors';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import {
  isFetchingResults,
  areMarkersAvailable,
  areMapBoundariesDefined,
  geoCoordinates,
  mapMarkers,
  selectGeoCoordinatesAndLocation,
  selectedMarker,
  selectSummaryCards,
  selectFocusedListingAndSitePlans,
  selectSitePlans
} from '../redux/selectors';
import { mapStateSlice } from '../redux/slices';
import {
  factsSearchParameters,
  featuresSearchParameters,
  locationSearchParameters,
  productSearchParameters,
  isNotCommunityLocationType
} from '../constants/searchParametersConstants';
import {
  mapContainerClicked$,
  mapContainerDoubleClicked$,
  mapMouseOver$
} from '../redux/observables';
import { isUserInteractingWithSitePlan } from '../services/sitePlan';
import getClassesToApply from '../services/unitMarkerService';
import MarkerType from '../enums/markerType';

const { actions } = mapStateSlice;
const mapContainer = document.querySelector('#nhs_map');
const zoomThresholdForMarkerGroups = 12;
const paidMarkersThreshold = 25;

/**
 * Handles the mouseover event for a map marker by positioning its tooltip based on the marker's
 * location relative to the map bounds.
 * The tooltip is dynamically positioned to ensure it stays visible within the viewport by adjusting
 * its horizontal (left/center/right) and vertical (above/below) alignment based on the marker's position
 * ratios within the map.
 */
const positionMarkerCard = (markerIcon) => {
  const marker = markerIcon?.closest('[data-marker-element]');
  if (!marker) {
    return;
  }
 
  const tooltip = marker.querySelector("[data-map-card]");
  if (!tooltip) {
    return;
  }
 
  const bounds = getMapBounds();
  if (!bounds) {
    return;
  }
 
  const { north, south, east, west } = bounds;
  const position = marker.getAttribute('position').split(',');
 
  const latitude = parseFloat(position[0]);
  const longitude = parseFloat(position[1]);
 
  const xRatio = (longitude - west) / (east - west);
  const yRatio = (north - latitude) / (north - south);
 
  const positioning = {
    horizontalPos: "center",
    verticalPos: "below"
  };
 
  // If the marker's vertical position ratio is above 0.55, show tooltip above marker
  // (i.e., in the upper 45% of the map)
  const VERTICAL_UPPER_THRESHOLD = 0.55;
  // If the marker's vertical position ratio is below 0.45, show tooltip below marker
  // (i.e., in the lower 45% of the map)
  const VERTICAL_LOWER_THRESHOLD = 0.45;
  // If the marker's horizontal position ratio is above 0.8, show tooltip to the left
  // (i.e., in the rightmost 20% of the map)
  const HORIZONTAL_LEFT_THRESHOLD = 0.8;
  // If the marker's horizontal position ratio is below 0.2, show tooltip to the right
  // (i.e., in the leftmost 20% of the map)
  const HORIZONTAL_RIGHT_THRESHOLD = 0.2;
 
  // NEW: Divide vertical space into three equal parts to detect the "central zone"
  // If the marker is in the middle third vertically, avoid diagonal up/down because tooltips get cut
  const CENTRAL_UPPER_BOUND = 0.33;
  const CENTRAL_LOWER_BOUND = 0.66;
 
  if (yRatio >= CENTRAL_UPPER_BOUND && yRatio <= CENTRAL_LOWER_BOUND) {
    positioning.verticalPos = "center";
 
    if (xRatio < 0.5) {
      positioning.horizontalPos = "right";
    } else {
      positioning.horizontalPos = "left";
    }
  } else {
    if (yRatio > VERTICAL_UPPER_THRESHOLD) {
      positioning.verticalPos = "above";
    } else if (yRatio < VERTICAL_LOWER_THRESHOLD) {
      positioning.verticalPos = "below";
    }
 
    if (xRatio < HORIZONTAL_RIGHT_THRESHOLD) {
      positioning.horizontalPos = "right";
    } else if (xRatio > HORIZONTAL_LEFT_THRESHOLD) {
      positioning.horizontalPos = "left";
    } else {
      positioning.horizontalPos = "center";
    }
  }
 
  tooltip.classList.remove(
    "nhs-c-tooltip--above",
    "nhs-c-tooltip--below",
    "nhs-c-tooltip--left",
    "nhs-c-tooltip--right",
    "nhs-c-tooltip--center"
  );
 
  tooltip.classList.add(`nhs-c-tooltip--${positioning.horizontalPos}`);
  tooltip.classList.add(`nhs-c-tooltip--${positioning.verticalPos}`);
};

/**
 * It takes a marker object as an argument, and dispatches an action to the Redux store that sets the
 * selectedMarker property of the state to the marker object
 * @param marker - The marker object that was clicked on.
 */
const setActiveMarker = marker => {
  if (!marker) {
    store.dispatch(actions.selectedMarker(marker));
    return;
  }

  /*
   * The selectedMarker in state needs the page number,
   * then before to dispach the selectedMarker is necesary set it the page number
   * from the mapMarker state.
   */
  const newSelectedMarker = Object.assign({}, marker);
  const state = store.getState();
  const { isFeaturedListing } = mapMarkers(state).find(
    housing => housing.id === newSelectedMarker.id
  );
  newSelectedMarker.isFeaturedListing = !!isFeaturedListing;
  store.dispatch(actions.selectedMarker(newSelectedMarker));

  document.body.dispatchEvent(
    new CustomEvent('markerPinCLicked', {
      detail: {
        marker: newSelectedMarker
      }
    })
  );
};

/**
 * It takes a marker object as an argument, and dispatches an action to the Redux store that sets the
 * selectedMarker property of the state to the marker object
 * @param marker - The marker object that was clicked on.
 */
const setActiveSummaryCard = listings => store.dispatch(actions.getSummaryCardsFulfilled(listings));

/**
 * Retrieves the DOM node for a marker icon based on the provided marker's id or communityId.
 *
 * - Searches for an element with [data-marker-icon] and a
 * - matching data-id attribute (first by id, then by communityId).
 * - Returns the marker DOM node if found, or null if not found.
 *
 * @param {number|string} param0.id - The unique id of the marker.
 * @param {number|string} param0.communityId - The community id of the marker.
 * @returns {Element|null} The marker DOM node if found, otherwise null.
 */
const getMarkerNode = ({ id, communityId }) => {
  const marker =
    document.querySelector(`[data-marker-icon][data-id="${id}"]`) ||
    document.querySelector(`[data-marker-icon][data-id="${communityId}"]`);

  return marker;
};

/* Dispatching an action to the store to fetch the markers. */
const fetchResults = () => {
  store.dispatch(
    actions.fetchResults({
      isFetchingResults: true,
      parameters: store.getState().searchParameters
    })
  );
};

/**
 * Removing active tooltip class and the hovered.
 */
const removeMarkerHovered = () => {
  const markerIcon = document.querySelector(
    `[data-marker-icon][data-hovered="true"]`
  );
  if (!markerIcon) {
    return;
  }
  markerIcon
    .closest('[data-geographic-key]')
    .removeAttribute('data-marker-hovered');
  markerIcon.classList.remove('nhs-mq-marker-hovered--large');
  delete markerIcon.dataset.hovered;

  const markerElement = markerIcon?.closest('[data-marker-element]');
  markerElement?.classList.remove('marker--active');
};

/**
 * Removing the active class to marker to close the marker card.
 */
const closeMarkerCard = () => {
  const markerIcon = document.querySelector(
    `[data-marker-icon][data-selected]`
  );
  if (!markerIcon) {
    return;
  }

  const markerElement = markerIcon.closest('[data-marker-element]');
  removeMarkerHovered();
  const { selected } = getClassesToApply({
    isAdult: markerIcon.dataset.isAdult === 'true',
    isMasterPlannedCommunity:
      markerIcon.dataset.isMasterPlannedCommunity === 'true',
    isSpotlight: markerIcon.dataset.isSpotlight === 'true',
    isSelected: true
  });
  selected.forEach(className => markerIcon.classList.remove(className));
  delete markerIcon.dataset.selected;
  markerElement?.classList.remove('active');
  markerElement?.classList?.remove('marker--active');
};

/**
 * Determines whether the listing detail should be opened for a given marker.
 *
 * Returns true if:
 * - Condition #1: the device breakpoint is not small.
 * - Condition #2: the marker's id matches the currently active marker's id from the store,
 *   meaning: it's the second click on the same marker.
 *
 * @param {Object} marker - The marker object to check.
 * @returns {boolean} True if the listing detail should be opened, otherwise false.
 */
const shouldOpenListingDetail = marker => {
  const currentState = store?.getState();
  const activeMarker = selectedMarker(currentState);
  const deviceBreakpoint = selectLayoutBreakpoint(currentState);

  return (
    deviceBreakpoint?.getValue() !== LayoutBreakpoint.Small.getValue()||
    marker.id === activeMarker?.id
  );
};

/**
 * A function that handler markers according to the user interaction of the map.
 */
const handleMarkerMapChanges = () => {
  const state = store.getState();
  const isMarkerSelected = !!state.mapState.selectedMarker;

  if (isMarkerSelected) {
    setActiveMarker(null);
  }

  closeMarkerCard();
};

/**
 * Add the active class to markert to open the marker card.
 */
const openMarkerCard = marker => {
  marker
    .closest('[data-geographic-key]')
    .setAttribute('data-marker-hovered', true);
  marker.classList?.add('nhs-mq-marker-hovered--large');
  const { selected, viewed } = getClassesToApply({
    isAdult: marker.dataset.isAdult === 'true',
    isMasterPlannedCommunity:
      marker.dataset.isMasterPlannedCommunity === 'true',
    isSpotlight: marker.dataset.isSpotlight === 'true',
    isSelected: true,
    isViewed: true
  });
  selected?.forEach(className => marker.classList.add(className));
  viewed?.forEach(className => marker.classList.add(className));
  marker.dataset.selected = true;
  marker.dataset.hovered = true;
  const markerElement = marker?.closest('[data-marker-element]');
  markerElement?.classList?.add('active');
  markerElement?.classList?.add('marker--active');
};

/**
 * Opening in new tab the url of the marker.
 */
const openUrl = (marker, event) => {
  const interactionType =
    event.type === 'dblclick' ? 'double_click' : 'slow_double_click';

  // emit custom event with marker data
  const customEvent = new CustomEvent('openMarkerUrl', {
    detail: {
      marker,
      interactionType
    }
  });

  document.body.dispatchEvent(customEvent);

  const url = marker.url;
  window.open(url, '_blank');
};

/**
 * A function that is called when a marker is clicked.
 *
 * Used to emit a new action set the selectedMarker to the currently active marker object.
 */
const onMarkerClicked = (marker, event) => {
  if (event.domEvent.srcElement.closest('[data-card]')) {
    return;
  }

  const googleAnalyticsDTO = marker.googleAnalyticsClickEvent;
  store.dispatch(uiStateSlice.actions.trackUserEvent({ googleAnalyticsDTO }));

  if (shouldOpenListingDetail(marker)) {
    openUrl(marker, event);
  }

  setActiveMarker(marker);
};

/**
 * hasExternalRepresentation function will calculate if the marker we are creating has a representation already in
 * the map, for instance: a site plan.
 * @param {Object} housingLocation the marker to be created.
 * @returns a flag indicating wheather the marker has a representation already in the map.
 */
const hasExternalRepresentation = housingLocation => {
  const state = store.getState();
  const sitePlans = selectSitePlans(state);
  return sitePlans.some(
    sitePlan => sitePlan.communityId === housingLocation.communityId
  );
};

/**
 * CreateMarker takes a housingLocation object and returns a new object with a markerPosition property
 * that has a lat and lng property.
 * @param housingLocation - the object that contains the Lat and Lng properties
 */
const configureMarker = housingLocation => ({
  events: {
    click: onMarkerClicked
  },
  googleAnalyticsClickEvent: housingLocation.getGoogleAnalyticsEventForClicks(),
  icon: housingLocation.getIcon(),
  id: housingLocation.id,
  markerPosition: {
    lat: +housingLocation.latitude,
    lng: +housingLocation.longitude
  },
  type: housingLocation.getType(),
  metaData: housingLocation.getHousingLocationMetaData(),
  zIndex: housingLocation.zIndex,
  url: housingLocation.getDetailUrl(),
  description: housingLocation.getDescription(),
  ignoreVisibilityUpdates: hasExternalRepresentation(housingLocation)
});

/**
 * Filters an array of marker objects to ensure only one marker per unique latitude/longitude pair is kept.
 * If multiple markers share the same coordinates, the marker with the highest zIndex is retained.
 *
 * @param {Array} markers - The array of marker objects to filter.
 * @returns {Array} A filtered array containing only the highest zIndex marker for each coordinate pair.
 */
const filterMarkersToPaint = markers => {
  const markersMap = new Map();

  markers.forEach(marker => {
    const key = `${marker.latitude},${marker.longitude}`;
    const existing = markersMap.get(key);

    if (!existing || marker.zIndex > existing.zIndex) {
      markersMap.set(key, marker);
    }
  });

  return Array.from(markersMap.values());
};

/**
 * Determines and returns the appropriate marker visibility strategy function based on the current 
 * zoom level and location type.
 * @param {number} zoom - The current zoom level of the map.
 * @returns {Function} A function that updates marker visibility, either `updateMarkersVisibility`
 * or a bound version of `updateMarkerGroupsByPriority`.
 */
function getMarkersVisibilityStrategy(zoom) {
  if (zoom >= zoomThresholdForMarkerGroups) {
    return updateMarkersVisibility;
  }

  const state = store.getState();
  const markers = mapMarkers(state);

  const isMarketOrCity =
    selectLocationType(state) === LocationType.Market.toString() ||
    selectLocationType(state) === LocationType.City.toString();

  // Business Rule: if there are few paid markers (<25), set threshold to 0 to show more markers
  const paidMarkers = markers.filter(m => !m.isBasic).length;
  const priorityThreshold = paidMarkers < paidMarkersThreshold ? 0 : 1;

  // Business Rule: if location is not Market or City → fixed threshold at 0
  const threshold = !isMarketOrCity ? 0 : priorityThreshold;

  return updateMarkerGroupsByPriority.bind(null, threshold);
}


/**
 * It takes the current markers and the new markers, and returns an object with two arrays: one with
 * the markers to add, and one with the markers to remove.
 *
 * The setTimeout is to allow the browser to split the task improving the performance.
 */
const updateMarkers = markersFromStore => {
  const zoom = getMapZoom();
  const updateMarkersVisibilityFunction = getMarkersVisibilityStrategy(zoom);

  const markers = filterMarkersToPaint(markersFromStore).map(configureMarker);
  setTimeout(() => paintMarker(markers));
  setTimeout(() => updateMarkersVisibilityFunction());
};

/**
 * requiresMarkerVisibilityChange determines if markers need to update their visibility, meaning: if we need to go
 * over ther markers to show/hide them based on the map viewport.
 * Function will return true if one of the following conditions happens:
 * * User has not changed map location, however the map changed its center, ie: user is dragging the map around.
 * * User is searching within map.
 * @param stateTransition - contains the output of a pairwise operation that brings previous and current state values.
 */
const requiresMarkerVisibilityChange = stateTransition => {
  const previousState = stateTransition[0];
  const currentState = stateTransition[1];
  return (
    (previousState.location === currentState.location &&
      previousState.coordinates !== currentState.coordinates) ||
    currentState.location.locationType ===
      LocationType.RectangularArea.toString()
  );
};

/**
 * Handles the selected marker by adding or removing the 'marker--active' class.
 * @param {Object} marker - The marker object.
 */
function handleSelectedMarker(marker) {
  const pastActive = document.querySelector('.marker--active');

  if (pastActive) {
    pastActive.classList.remove('marker--active');
  }

  if (marker) {
    const markers = document.querySelectorAll(
      `[data-id="${marker.id}"][data-type="${marker.type}"]`
    );

    markers.forEach(icon => {
      const container = icon?.closest('[data-marker-element]');
      container?.classList.add('marker--active');
    });
  }
}

/**
 * Handles a double click event on a map marker element.
 * @param {Event} event - The double click event object.
 */
function handleMarkerMapDoubleClick(event) {
  const state = store.getState()?.mapState;
  const marker = state?.selectedMarker;

  openUrl(marker, event);
  removeMarkerHovered();
}

/**
 * Ensures a hovered marker is created and displayed on the map for the given focused listing.
 *
 * - Checks if a marker DOM node exists for the focused listing (by id or communityId).
 * - If not, searches the markers from the store for a matching marker by id or communityId and type.
 * - If a matching marker is found, creates and displays a hovered marker for it.
 *
 * @param {Object} focusedListing - The listing object to focus/hover.
 */
const createHoveredMarkerOnDemand = focusedListing => {
  const { id, communityId } = focusedListing;
  const marker = getMarkerNode(focusedListing);

  if (!marker && id && communityId) {
    const markersFromStore = mapMarkers(store.getState());
    const listingMarker =
      markersFromStore.find(element => element.id === +id) ??
      markersFromStore.find(
        element =>
          element.communityId === +communityId &&
          element.type === MarkerType.BuildablePlan.toString()
      );

    if (listingMarker) {
      createHoveredMarker(configureMarker(listingMarker));
    }
  }
};

/* 
Subscribe to the store$ observable to listen for changes. When markers are available, map the results
property and compare if the value has changed from the previous execution. Then filters to see if the 
map boundaries are defined. 

This subscription handles the scenario when the results have changed.
 */
store$
  .pipe(
    filter(areMarkersAvailable),
    filter(areMapBoundariesDefined),
    map(mapMarkers),
    distinctUntilChanged()
  )
  .subscribe(markers => {
    const state = store.getState();
    const summaryCard = selectSummaryCards(state);

    setTimeout(removeMarkers, 0);
    setTimeout(updateMarkers.bind(null, markers), 0);

    // only clear the summary card if available.
    if (summaryCard) {
      setActiveSummaryCard(null);
    }
  });

/* 
Subscribe to the store$ observable to listen for changes. When markers the map boundaries are available, 
map the coordinates property and compare if the value has changed from the previous execution.
Then filters to see if there are results defined. 

This subscription handles the scenario when the map coordinates have changed.
 */
store$
  .pipe(
    filter(areMapBoundariesDefined),
    filter(state => !isFetchingResults(state)),
    filter(areMarkersAvailable),
    map(selectGeoCoordinatesAndLocation),
    distinctUntilKeyChanged('coordinates'),
    pairwise(),
    filter(requiresMarkerVisibilityChange),
    map(() => getMapZoom())
  )
  .subscribe((zoom) => {
    const updateMarkersVisibilityFunction = getMarkersVisibilityStrategy(zoom);
    updateMarkersVisibilityFunction();
  });

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in facts leaf change, fetch the new results for map.
 */
const facts$ = store$.pipe(map(factsSearchParameters), distinctUntilChanged());

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in features leaf change, fetch the new results for map.
 */
const features$ = store$.pipe(
  map(featuresSearchParameters),
  distinctUntilChanged()
);

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in location leaf change, fetch the new results for map.
 */
const location$ = store$.pipe(
  filter(isNotCommunityLocationType),
  map(locationSearchParameters),
  distinctUntilChanged()
);

/** Subscribe to the store$ observable to listen for changes
 * When search parameters in product leaf change, fetch the new results for map.
 */
const product$ = store$.pipe(
  map(productSearchParameters),
  distinctUntilChanged()
);

/**
 * If some stream has changed, fetch the results.
 * Don't remove the debounceTime because it helps to prevent duplicate the fetch result request if
 * some leaf change at the same time.
 */
combineLatest([facts$, features$, location$, product$])
  .pipe(debounceTime(100))
  .subscribe(fetchResults);

/**
 * Subscribing to the store$ observable to listen for changes.
 * When the selectedMarker changes, map the selectedMarker and compare if the value has changed to valid value.
 * If that is the case run the clearActiveMarker function when the value is truly when an marker is active,
 * otherwise run setActiveMarker.
 */
const selectedMarker$ = store$.pipe(
  map(selectedMarker),
  distinctUntilChanged()
);

/**
 * Observable stream that emits marker elements when the mouse hovers over them.
 * 
 * This observable filters mouse-over events from the map to detect when a user
 * hovers over a marker element (identified by the `data-marker-icon` attribute).
 * Only valid marker elements are emitted; null or undefined values are filtered out.
 */
const onMouseHoveringMarker$ = mapMouseOver$.pipe(
  tap(event => {
    const previous = document.querySelector('[data-marker-hovered]');
    const current = event.target
      .closest('[data-marker-element]')
      ?.querySelector('[data-marker-icon]');

    // If hovered marker changed → close previous tooltip
    const isNewMarker = previous && previous !== current;

    if (isNewMarker) {
      closeMarkerCard();
    }
  }),
  debounceTime(500),
  map(event => event.target.closest('[data-marker-icon]')),
  filter(Boolean),
  tap(positionMarkerCard)
);

/**
 * Subscribes to the `onMouseHoveringMarker$` observable in order to position the map hover card in the best
 * possible position according to the marker position in the map viewport.
 */
onMouseHoveringMarker$.subscribe(openMarkerCard);

/**
 * Closes the marker card when the selectedMarker$ observable emits a value.
 * This is used to close any previously opened card.
 */
selectedMarker$.subscribe(closeMarkerCard);

/**
 * Subscribes to the `selectedMarker$` observable and opens a marker card when a marker with a valid ID is selected.
 * The marker icon element is extracted from the marker information and used to open the marker card.
 */
selectedMarker$
  .pipe(
    filter(marker => Boolean(marker?.metaData)),
    map(marker =>
      getMarkerNode({
        id: marker.metaData.id,
        communityId: marker.metaData.communityId
      })
    ),
    filter(Boolean),
    tap(positionMarkerCard)
  )
  .subscribe(openMarkerCard);

/**
 * Subscribing to the store$ observable to listen for changes.
 * When the map coordinates have changed, map the coordinates and compare if the value has changed.
 * If that is the case run the handleMarkerMapChanges function.
 */
store$
  .pipe(map(geoCoordinates), distinctUntilChanged())
  .subscribe(handleMarkerMapChanges);

store$
  .pipe(map(selectedMarker), distinctUntilChanged())
  .subscribe(handleSelectedMarker);

/**
 * By subscribing to selectFocusedListing, we can effectively monitor changes in the Point of Interest listing.
 * This enables us to dynamically apply styles to the relevant marker, providing an intuitive visual indication
 * to the user regarding the location of a listing.
 */
store$
  .pipe(
    filter(mapMarkers),
    map(selectFocusedListingAndSitePlans),
    distinctUntilKeyChanged('focusedListing'),
    filter(
      ({ focusedListing, sitePlans }) =>
        !sitePlans.some(
          sitePlan => sitePlan.communityId === Number(focusedListing?.communityId)
        )
    ),
    tap(closeMarkerCard),
    tap(removeHoveredMarker),
    tap(({ focusedListing }) => createHoveredMarkerOnDemand(focusedListing)),
    debounceTime(100),
    map(({ focusedListing }) => getMarkerNode(focusedListing)),
    filter(Boolean),
    tap(positionMarkerCard)
  )
  .subscribe(openMarkerCard);

/**
 * An observable that emits when the map container is double-clicked, and the click target is an element with the
 * `data-marker-element` attribute.
 */
const markerDoubleClicked$ = mapContainerDoubleClicked$.pipe(
  filter(event => event.target.closest('[data-marker-element]'))
);

/**
 * Listens for a double click event on the map container element and subscribes to the handleMarkerMapDoubleClick
 * function. This allows the application to handle double click events on map markers and open the associated URL.
 */

markerDoubleClicked$.subscribe(handleMarkerMapDoubleClick);
/**
 * Listening for a click event on the map.
 */
mapContainer.addEventListener('mapHide', handleMarkerMapChanges);

mapContainerClicked$
  .pipe(
    filter(event => !isUserInteractingWithSitePlan(event)),
    filter(({ target }) => !target.closest('[data-marker-icon]'))
  )
  .subscribe(handleMarkerMapChanges);

/**
 * Listens for the 'markersUpdated' custom event on the document body and updates the content of affected markers.
 *
 * - When the 'markersUpdated' event is dispatched, receives the updated markers in event.detail.
 * - For each updated marker, reconfigures the marker (e.g., to get the latest icon HTML).
 * - Calls updateMarkerContent with the marker's id and new icon HTML string to update the marker's display on the map.
 *
 * This ensures that any changes to marker data (such as site plan updates or other dynamic changes)
 * are immediately reflected in the map UI without a full re-render.
 */
fromEvent(document.body, 'markersUpdated').subscribe(event => {
  const updatedMarkers = event.detail;
  if (Array.isArray(updatedMarkers)) {
    updatedMarkers.forEach(updatedMarker => {
      const { icon } = configureMarker(updatedMarker);
      requestAnimationFrame(() => updateMarkerContent(updatedMarker.id, icon));
    });
  }
});